import { Heading } from 'blaze-ui/Heading/Heading'
import { Spinner } from 'common/Spinner/Spinner'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import Styles from './BrandWizardIntegrationSelection.module.scss'
import { IntegrationCard } from './IntegrationCard'
import { ExternalIntegration, FacebookProfile, InstagramProfile } from 'api/clients/ExternalIntegrationsClient'
import { NewIntegrationCard } from './NewIntegrationCard'
import Collapse from 'react-css-collapse'
import { useState } from 'react'
import { Button } from 'blaze-ui/Button'
import { useQuery } from 'react-query'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useAuth } from 'hooks/UseAuth'
import { isAlmanacEmail } from 'utils/featureFlagUserGroups'
import { VISUAL_ANALYSIS_SUPPORTED_INTEGRATIONS } from './constants'
import cx from 'classnames'

interface Props {
  selectedIntegration?: ExternalIntegration
  onSelectIntegration: (integration: ExternalIntegration) => void
}

const NoIntegrations = () => (
  <Heading level={4}>
    Connect your social account, and we will create Brand Styles for you based on your content!
  </Heading>
)

const getIntegrationLink = (integration: ExternalIntegration) => {
  switch (integration.provider) {
    case 'instagram':
      return `https://www.instagram.com/${(integration.additional_data as InstagramProfile).username}`
    case 'facebook':
      return `https://www.facebook.com/${(integration.additional_data as FacebookProfile).id}`
    default:
      return undefined
  }
}

export const Body = ({ selectedIntegration, onSelectIntegration }: Props) => {
  const { data, isLoading } = useExternalIntegrations()
  const auth = useAuth()
  const [showTestIntegrations, setShowTestIntegrations] = useState(false)
  const testIntegrationsQuery = useQuery<{ external_integrations: ExternalIntegration[] }>(
    ['test_integrations'],
    async () => {
      const res = await sendToApi('/instagram_integrations')
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }
      return res.data
    },
    {
      refetchOnWindowFocus: false,
    },
  )

  if (isLoading) {
    return <Spinner className={Styles.spinner} />
  }

  if (!data) {
    return null
  }

  const integrations = data.external_integrations.filter((integration) =>
    VISUAL_ANALYSIS_SUPPORTED_INTEGRATIONS.includes(integration.provider),
  )

  const canSeeTestIntegrations = auth?.email && isAlmanacEmail(auth?.email)

  if (integrations.length === 0 && !canSeeTestIntegrations) {
    return <NoIntegrations />
  }

  return (
    <div className={cx(Styles.body, { [Styles.withTestIntegrations]: canSeeTestIntegrations })}>
      <Heading level={4}>
        Select or connect your social account, and we will create Brand Styles for you based on your content!
      </Heading>
      <div className={Styles.integrationsGrid}>
        {integrations.map((integration) => (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            onSelect={() => onSelectIntegration(integration)}
            isSelected={selectedIntegration?.id === integration.id}
          />
        ))}
        <NewIntegrationCard />
      </div>

      {canSeeTestIntegrations && (
        <>
          <Button size="xs" variant="tertiary" onPress={() => setShowTestIntegrations(!showTestIntegrations)}>
            Show test integrations
          </Button>
          <Collapse isOpen={showTestIntegrations}>
            <div className={Styles.integrationsGrid}>
              {testIntegrationsQuery.data?.external_integrations.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  onSelect={() => onSelectIntegration(integration)}
                  isSelected={selectedIntegration?.id === integration.id}
                  link={getIntegrationLink(integration)}
                />
              ))}
            </div>
          </Collapse>
        </>
      )}
    </div>
  )
}
